import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUITheme } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import { DCUIThemingThemeValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";
import { FileInputFormGroup } from "@containers/common/file-input-form-group/file-input-form-group";
import { ResourceCategoryTypes } from "@enums/resource-category-types";

type DCUIThemingThemeHeaderLogoSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiTheme: DCUITheme;
    inheritedDCUITheme: DCUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: DCUIThemingThemeValidationErrors;
    updateDCUIThemePropertyValue: (value: DCUITheme) => void;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
};

export const DCUIThemingThemeHeaderLogoSection = (props: DCUIThemingThemeHeaderLogoSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiTheme,
        inheritedDCUITheme,
        pageLevel,
        validationErrors,
        updateDCUIThemePropertyValue,
        uploadFileToS3
    } = props;
    const [headerLogoUploadInProgress, setHeaderLogoUploadInProgress] = useState(false);

    const handleAddHeaderLogoChange = (newValue: string) => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                showLogo: isStringTrue(newValue)
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleResetAddHeaderLogo = () => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                showLogo: inheritedDCUITheme.header.showLogo
            }
        };

        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleHeaderLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];

            setHeaderLogoUploadInProgress(true);
            const uploadResult = await uploadFileToS3(file, ResourceCategoryTypes.DCUI_HEADER_LOGO);
            if (uploadResult) {
                updateDCUIThemePropertyValue({
                    ...dcuiTheme,
                    header: {
                        ...dcuiTheme.header,
                        logoUrl: uploadResult
                    }
                });
            }
            setHeaderLogoUploadInProgress(false);
        }
    };

    const handleResetHeaderLogo = () => {
        updateDCUIThemePropertyValue({
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                logoUrl: inheritedDCUITheme.header.logoUrl
            }
        });
    };

    const handleHeaderLogoPositionChange = (newValue: SelectOption) => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                alignLogo: newValue.label
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleResetHeaderLogoPosition = () => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            header: {
                ...dcuiTheme.header,
                alignLogo: inheritedDCUITheme.header.alignLogo
            }
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const headerLogoPositionOptions = useMemo(() => {
        return [
            { label: "center", value: "center" },
            { label: "left", value: "left" },
            { label: "right", value: "right" },
        ];
    }, []);

    const selectedHeaderLogoPositionOption = useMemo(() => {
        if (dcuiTheme.header.alignLogo) {
            return headerLogoPositionOptions.find(po => po.label === dcuiTheme.header.alignLogo);
        }
        return null;
    }, [dcuiTheme.header.alignLogo]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUITheme.header.showLogo}
                currentThemingPropertyValue={dcuiTheme.header.showLogo}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`dcui-theming-header-add-header-logo-radio-input`}
                    automationId={`dcui-theming-header-add-header-logo-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerLogo.inputs.addHeaderLogo.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAddHeaderLogoChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={dcuiTheme.header.showLogo ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerLogo.inputs.addHeaderLogo.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAddHeaderLogo}
                />
            </ThemingFormGroupWrapper>
            {dcuiTheme.header.showLogo && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedDCUITheme.header.logoUrl}
                        currentThemingPropertyValue={dcuiTheme.header.logoUrl}
                        pageLevel={pageLevel}
                    >
                        <FileInputFormGroup
                            controlId={"dcui-theming-header-header-logo-input"}
                            automationId={"dcui-theming-header-header-logo-input"}
                            label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerLogo.inputs.headerLogo.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerLogo.inputs.headerLogo.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleHeaderLogoChange}
                            value={dcuiTheme.header.logoUrl || ""}
                            accept={"image/png, image/gif, image/jpeg, image/jpg"}
                            disabled={isDisabled}
                            uploadInProgress={headerLogoUploadInProgress}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerLogo.inputs.headerLogo.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderLogo}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.headerLogoError}
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedDCUITheme.header.alignLogo}
                        currentThemingPropertyValue={dcuiTheme.header.alignLogo}
                        pageLevel={pageLevel}
                    >
                        <SelectFormGroup
                            controlId={"dcui-theming-header-header-text-postition-select-input"}
                            automationId={"dcui-theming-header-header-text-position-select-input"}
                            label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerLogo.inputs.headerLogoPosition.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerLogo.inputs.headerLogoPosition.label}
                            disabled={isDisabled}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleHeaderLogoPositionChange}
                            options={headerLogoPositionOptions}
                            selectedOption={selectedHeaderLogoPositionOption}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.theme.sections.headerLogo.inputs.headerLogoPosition.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderLogoPosition}
                        />
                    </ThemingFormGroupWrapper>
                </>
            )}
        </div>
    );
};